import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  BOTTOM_NAV_PATHS,
  findNavItem,
  visibleGroups,
  type NavItem,
} from "./nav-items";

/**
 * Barra di navigazione del pollice (solo sotto md).
 *
 * Due strati: la riga dei bottoni ha altezza fissa (--bottom-nav-row) e la
 * safe area sta SOTTO, non dentro. Prima erano sommate dentro lo stesso
 * `h-14`, così su iPhone la riga restava schiacciata a una ventina di pixel
 * e icone e testo diventavano illeggibili.
 *
 * Quattro destinazioni fisse + "Altro", che apre l'indice completo: meglio
 * cinque voci comode che diciotto sezioni irraggiungibili dal basso.
 */
export function BottomNav() {
  const { canAccess, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = BOTTOM_NAV_PATHS.map(findNavItem).filter(
    (i): i is NavItem => !!i && canAccess(i.module),
  );
  const groups = visibleGroups(isSuperAdmin, canAccess);
  if (items.length === 0) return null;

  // "Altro" resta acceso quando sei in una sezione che non ha uno slot suo
  const inSheetSection = !items.some((i) =>
    i.end ? location.pathname === i.to : location.pathname.startsWith(i.to),
  );

  return (
    <>
      <nav
        aria-label="Navigazione principale"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-primary/15 bg-background/80 backdrop-blur-xl md:hidden"
        style={{ paddingBottom: "var(--safe-bottom)" }}
      >
        {/* Filo d'oro sul bordo superiore: la firma della casa */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
        />
        <div className="flex" style={{ height: "var(--bottom-nav-row)" }}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className="relative flex min-w-0 flex-1 select-none flex-col items-center justify-center gap-1 px-0.5 transition-transform duration-fast ease-palazzo active:scale-95"
            >
              {({ isActive }) => (
                <NavContent item={it} isActive={isActive && !menuOpen} />
              )}
            </NavLink>
          ))}

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            className="relative flex min-w-0 flex-1 select-none flex-col items-center justify-center gap-1 px-0.5 transition-transform duration-fast ease-palazzo active:scale-95"
          >
            <NavContent
              item={{ to: "", label: "Altro", icon: LayoutGrid, module: "" }}
              isActive={menuOpen || inSheetSection}
            />
          </button>
        </div>
      </nav>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="bottom"
          hideCloseButton
          className="scroll-isolate scroll-touch max-h-[85vh] overflow-y-auto rounded-t-[20px] border-t border-primary/20 p-0"
        >
          <SheetTitle className="sr-only">Tutte le sezioni</SheetTitle>

          {/* Maniglia: dice "trascinami", come in una app nativa */}
          <div className="sticky top-0 z-10 flex justify-center bg-sidebar/95 pb-2 pt-3 backdrop-blur-xl">
            <span aria-hidden className="h-1 w-10 rounded-full bg-white/20" />
          </div>

          <div
            className="space-y-5 px-4"
            style={{ paddingBottom: "calc(1rem + var(--safe-bottom))" }}
          >
            {groups.map((group) => (
              <div key={group.label}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-primary/50">
                    {group.label}
                  </span>
                  <span
                    aria-hidden
                    className="h-px min-w-2 flex-1 bg-gradient-to-r from-primary/25 to-transparent"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 min-[420px]:grid-cols-4">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex min-h-[76px] flex-col items-center justify-center gap-1.5 rounded-xl border p-2 text-center",
                          "transition-colors duration-fast ease-palazzo active:scale-95",
                          isActive
                            ? "border-primary/30 bg-primary/10 text-primary"
                            : "border-white/10 bg-white/[0.03] text-muted-foreground",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            className={cn(
                              "h-5 w-5 shrink-0",
                              isActive ? "text-primary" : "text-gold-soft/70",
                            )}
                          />
                          <span className="w-full truncate text-[11px] font-medium leading-tight">
                            {item.short ?? item.label}
                          </span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function NavContent({ item, isActive }: { item: NavItem; isActive: boolean }) {
  return (
    <>
      {isActive && (
        <motion.span
          layoutId="bottomnav-active"
          className="absolute inset-x-1.5 top-1 h-9 rounded-xl bg-gradient-to-b from-primary/20 to-primary/5 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.22)]"
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
      )}
      <item.icon
        className={cn(
          "relative z-10 h-[22px] w-[22px] shrink-0",
          isActive
            ? "text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.55)]"
            : "text-muted-foreground",
        )}
      />
      <span
        className={cn(
          "relative z-10 w-full truncate px-0.5 text-center text-[11px] font-medium leading-none",
          isActive ? "text-primary" : "text-muted-foreground",
        )}
      >
        {item.short ?? item.label}
      </span>
    </>
  );
}
